"use client";

import { useDesktopAnimations } from "@/lib/breakpoints";
import { lazyContactResolver, submitContactForm } from "@/lib/lazyContactForm";
import { toast } from "react-toastify";

import Image from "next/image";
import { FaEnvelope, FaPhoneAlt, FaWhatsapp } from "react-icons/fa";
import Input from "../ui/Input";
import type { ContactFormValues } from "@/app/validations/contactSchema";
import { useForm, useWatch } from "react-hook-form";
import { useState } from "react";
import { motion } from "framer-motion";
import GradientButton from "@/components/ui/GradientButton";
import ResponsiveTurnstile from "@/components/common/ResponsiveTurnstile";
import CountryPhoneInput from "@/components/common/CountryPhoneInput";
const EASE = [0.25, 1, 0.5, 1] as const;

const contactItems = [
  {
    label: "Email",
    value: "info@thedezine.in",
    href: "mailto:info@thedezine.in",
    icon: FaEnvelope,
    color: "text-red-500",
  },
  {
    label: "Call",
    value: "(+91) 992 452 3136",
    href: "tel:+919924523136",
    icon: FaPhoneAlt,
    color: "text-green-500",
  },
  {
    label: "WhatsApp",
    value: "(+91) 992 452 3136",
    href: "https://wa.me/919924523136",
    icon: FaWhatsapp,
    color: "text-green-500 dark:text-green-400",
  },
];

export const ContactSection = () => {
  const desktopAnimations = useDesktopAnimations();
  const {
    register,
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormValues>({
    defaultValues: { name: "", email: "", mobile: "", message: "" },
    resolver: lazyContactResolver,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const mobile = useWatch({ control, name: "mobile" }) ?? "";

  const onSubmit = async (data: ContactFormValues) => {
    if (loading) return;
    try {
      const isDev = process.env.NODE_ENV === "development";
      if (!captchaToken && !isDev) {
        toast.error("Please complete the captcha");
        return;
      }

      setLoading(true);
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value as string);
        }
      });

      if (captchaToken) {
        formData.append("captcha_token", captchaToken);
      }

      formData.append("type", "contact");

      const res = await submitContactForm(formData);
      if (res?.success === true) {
        toast.success(res?.message || "Submitted successfully!");
        reset();
        setCaptchaToken(null);
      } else {
        toast.error(res?.message || "Failed to submit");
      }
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  const allowOnlyText = (e: React.FormEvent<HTMLInputElement>) => {
    e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z\s]/g, "");
  };

  return (
    <section
      data-footer-parallax-target
      className="relative z-10 overflow-hidden py-16 transition-colors [clip-path:inset(0)] md:py-20 dark:bg-gray-950"
    >
      {/* Same look as the old `md:bg-fixed` background, but `background-attachment:
          fixed` forces the browser to repaint the whole section on every scroll
          frame. A viewport-fixed layer clipped by the section's clip-path gives
          the identical parallax while staying on the compositor. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-contact-footer bg-cover bg-center bg-no-repeat md:fixed md:h-lvh"
      />
      <div className="pointer-events-none absolute inset-0 bg-black/60 dark:bg-black/80" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 gap-10 px-4 sm:px-6 md:gap-12 lg:grid-cols-12 lg:gap-0 lg:px-8">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="lg:col-span-5 lg:px-4 lg:pt-8"
        >
          <span className="mb-4 block font-open-sans text-base font-normal uppercase tracking-wide text-pink-600">
            Inquiry
          </span>
          <h2 className="font-poppins text-3xl font-semibold leading-tight text-white md:text-4xl dark:text-white">
            Let&apos;s get in touch
          </h2>

          {/* <div className="mt-8 flex items-center gap-4">
            <Image
              src="/images/images/ceo.png"
              alt="Pratik Gandhi"
              width={80}
              height={80}
              loading="lazy"
              className="size-16 rounded-xl object-cover shadow-lg sm:size-20"
            />
            <div>
              <h3 className="font-poppins text-xl font-semibold text-white dark:text-white">
                Pratik Gandhi
              </h3>
              <p className="mt-1 font-open-sans text-base text-white/80 dark:text-gray-400">
                Founder & CEO
              </p>
            </div>
          </div> */}

          <div className="mt-6 space-y-4 sm:mt-10 sm:space-y-8">
            {contactItems.map((item) => {
              const Icon = item.icon;
              return (
                <a
                  key={item.label}
                  href={item.href}
                  className="group flex min-h-16 w-full cursor-pointer items-center rounded-md border border-red-200 bg-red-100 px-3 py-3 transition-[box-shadow,border-color,transform] duration-300 hover:-translate-y-1 hover:shadow-xl sm:min-h-20 sm:px-5 sm:py-4 dark:border-gray-700 dark:bg-gray-800"
                >
                  <span
                    className={`flex w-10 shrink-0 items-center justify-center text-lg sm:w-12 sm:text-xl ${item.color}`}
                  >
                    <Icon />
                  </span>
                  <span className="min-w-0 flex-1 border-l border-red-300/60 pl-3 sm:pl-5">
                    <span className="block font-open-sans text-xs font-light uppercase tracking-wider text-primary dark:text-gray-300 sm:text-sm sm:tracking-widest">
                      {item.label}
                    </span>
                    <span className="mt-0.5 font-open-sans block truncate font-poppins text-base font-semibold leading-tight text-primary dark:text-white sm:mt-1 sm:text-xl sm:leading-8">
                      {item.value}
                    </span>
                  </span>
                </a>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="lg:col-span-6 lg:col-start-7 lg:px-4"
        >
          <div className="rounded-xl bg-neutral-100 p-5 shadow-xl sm:p-8 lg:p-12 dark:bg-gray-900">
            <h3 className="font-poppins text-3xl font-semibold leading-tight text-primary md:text-4xl dark:text-white">
             Hey there! :)
            </h3>

            <form
              className="mt-7 space-y-5 sm:mt-8"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <Input
                label="Full Name"
                labelClassName="!mb-4 !font-open-sans !text-sm !font-medium !text-slate-700 dark:!text-slate-200"
                requiredField
                type="text"
                placeholder="Enter full name"
                {...register("name")}
                onInput={allowOnlyText}
                error={errors.name?.message}
                reserveErrorSpace={false}
                className="!h-13 !rounded-xl !border-0 !border-b-0 !bg-white !px-5 !font-open-sans !text-sm !text-slate-900 placeholder:!text-slate-400 focus:!border-b-0 focus:!border-transparent focus:!ring-0 dark:!bg-gray-800 dark:!text-slate-100 dark:placeholder:!text-slate-500"
              />

              <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
                <Input
                  label="Email"
                  labelClassName="!mb-4 !font-open-sans !text-sm !font-medium !text-slate-700 dark:!text-slate-200"
                  requiredField
                  type="email"
                  placeholder="Enter email"
                  {...register("email")}
                  error={errors.email?.message}
                  reserveErrorSpace={false}
                  className="!h-13 !rounded-xl !border-0 !border-b-0 !bg-white !px-5 !font-open-sans !text-sm !text-slate-900 placeholder:!text-slate-400 focus:!border-b-0 focus:!border-transparent focus:!ring-0 dark:!bg-gray-800 dark:!text-slate-100 dark:placeholder:!text-slate-500"
                />
                <div>
                  <label className="mb-4 block font-open-sans text-sm font-medium text-slate-700 dark:text-slate-200">
                    Mobile
                  </label>
                  <input
                    type="hidden"
                    {...register("mobile")}
                    className="rounded-xl"
                  />
                  <CountryPhoneInput
                    id="inquiry-mobile"
                    name="inquiry-mobile-input"
                    value={mobile}
                    onChange={(value) =>
                      setValue("mobile", value, {
                        shouldDirty: true,
                        shouldValidate: Boolean(errors.mobile),
                      })
                    }
                    placeholder="Enter mobile number"
                    variant="pill"
                    className="rounded-xl"
                  />
                  {errors.mobile?.message && (
                    <p className="mt-2 font-open-sans text-sm leading-4 text-red-500">
                      {errors.mobile.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-4 block font-open-sans text-sm font-medium text-slate-700 dark:text-slate-200">
                  Message <span className="text-red-500">*</span>
                </label>

                <textarea
                  rows={4}
                  maxLength={500}
                  placeholder="Enter message"
                  {...register("message")}
                  className="h-25 w-full resize-none rounded-xl !border-0 !border-b-0 bg-white p-5 font-open-sans text-sm text-slate-900 outline-none placeholder:font-open-sans placeholder:text-sm placeholder:text-slate-400 focus:!border-0 focus:!border-b-0 focus:ring-0 dark:bg-gray-800 dark:text-slate-100 dark:placeholder:text-slate-500"
                />

                {errors.message && (
                  <p className="mt-2 text-sm text-red-500">
                    {errors.message.message}
                  </p>
                )}
              </div>
              <div className="w-full min-w-0 max-w-full">
                <ResponsiveTurnstile
                  siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
                  onSuccess={(token) => setCaptchaToken(token)}
                  onExpire={() => setCaptchaToken(null)}
                />
              </div>
              <GradientButton
                type="submit"
                variant="inquiry-blue"
                disabled={loading}
                className="w-full !font-medium"
              >
                {loading ? "Submitting..." : "Send your inquiry"}
              </GradientButton>
              <p className="text-center text-xs text-gray-500">
                We hate spam, and we respect your privacy.
              </p>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
